import { ChangeDetectionStrategy, Component, computed, inject, Input, input, OnInit, output, signal } from '@angular/core'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { InviteTeamMemberPayload, OrganizationUser, USER_ROLES } from '@app/v2/shared/types/user.type'
import { USER_STATUS_ACTION } from './constants/status-dropdown-options'
import { TEAM_MEMBER_ACTIONS_REQUIRING_CONFIRMATION, TEAM_MEMBER_API_ACTION, TeamMemberActionPayload } from './constants/team-member-action'
import { INVITE_TEAM_MEMBERS_MODAL_ID, InviteTeamMembersModalComponent } from './invite-team-members-modal/invite-team-members-modal.component'
import { ROLES_PRIVILEGE_TABLE_MODAL_ID, RolesTableComponent } from './roles-table/roles-table.component'
import { TEAM_MEMBER_ACTION_MODAL_ID, TeamMemberActionModalComponent } from './team-member-action-modal/team-member-action-modal.component'
import { TEAM_MEMBER_ROLE_UPDATE_MODAL_ID, TeamMemberRoleUpdateModalComponent } from './team-member-role-update-modal/team-member-role-update-modal.component'
import { UsersListComponent } from './users-list/users-list.component'

@Component({
  selector: 'app-manage-team',
  imports: [RolesTableComponent, UsersListComponent, InviteTeamMembersModalComponent, TeamMemberActionModalComponent, TeamMemberRoleUpdateModalComponent, ModalComponent],
  standalone: true,
  templateUrl: './manage-team.component.html',
  styleUrl: './manage-team.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageTeamComponent implements OnInit {
  public users = input<OrganizationUser[]>([])
  public isLoadingUsers = input<boolean>(false)
  public isLoadingMoreUsers = input<boolean>(false)
  public hasMoreUsers = input<boolean>(false)
  public isUpdatingTeamMember = input<boolean>(false)
  public readonly isInvitingTeamMember = signal(false)

  @Input()
  set isInvitingTeamMemberInput(value: boolean) {
    this.isInvitingTeamMember.set(value)
  }

  public loadUsers = output<void>()
  public loadMoreUsers = output<void>()
  public inviteTeamMember = output<InviteTeamMemberPayload>()
  public updateTeamMember = output<TeamMemberActionPayload>()
  public updateTeamMemberRole = output<{ id: number; role: USER_ROLES }>()

  public showRolesTable = signal<boolean>(false)
  public showInviteTeamMembersModal = signal<boolean>(false)
  public readonly cancelInvitationModalId = 'cancel-team-invitation-modal'
  public showTeamMemberActionModal = signal<boolean>(false)
  public showTeamMemberRoleUpdateModal = signal<boolean>(false)
  public pendingTeamMemberAction = signal<{ user: OrganizationUser; action: USER_STATUS_ACTION } | null>(null)
  public pendingCancelInvitation = signal<OrganizationUser | null>(null)
  public pendingTeamMemberRoleUpdate = signal<{ user: OrganizationUser; role: USER_ROLES } | null>(null)
  public readonly cancelInvitationDisplayName = computed(() => {
    const user = this.pendingCancelInvitation()
    if (!user) return ''
    return user.fullname?.trim() || user.email
  })
  private readonly _modalService = inject(ModalService)

  ngOnInit(): void {
    this.loadUsers.emit()
  }

  /**
   * Opens the roles privilege table modal
   */
  public openRolesPrivilegeTable(): void {
    this.showRolesTable.set(true)
  }

  /**
   * Destroys the roles table after the modal is closed.
   */
  public onRolesTableClosed(): void {
    this.showRolesTable.set(false)
    this._modalService.unregister(ROLES_PRIVILEGE_TABLE_MODAL_ID)
  }

  /**
   * Opens the invite team members modal
   */
  public openInviteTeamMembersModal(): void {
    this.showInviteTeamMembersModal.set(true)
  }

  /**
   * Closes the invite team members modal
   */
  public onInviteTeamMembersModalClosed(): void {
    this.showInviteTeamMembersModal.set(false)
    this._modalService.unregister(INVITE_TEAM_MEMBERS_MODAL_ID)
  }

  /**
   * Closes invite team members modal programmatically.
   */
  public closeInviteTeamMembersModal(): void {
    this.onInviteTeamMembersModalClosed()
  }

  /**
   * Forwards invite payload to parent container.
   * @param {InviteTeamMemberPayload} payload Invite team member payload
   * @returns {void}
   */
  public onInviteTeamMember(payload: InviteTeamMemberPayload): void {
    this.inviteTeamMember.emit(payload)
  }

  /**
   * Handles status action selection from a user card.
   * @param payload Selected user and action
   */
  public onStatusActionSelected(payload: { user: OrganizationUser; action: USER_STATUS_ACTION }): void {
    if (this.isUpdatingTeamMember() || !TEAM_MEMBER_API_ACTION[payload.action]) return
    if (!TEAM_MEMBER_ACTIONS_REQUIRING_CONFIRMATION.has(payload.action)) {
      this.updateTeamMember.emit({ id: payload.user.id, action: TEAM_MEMBER_API_ACTION[payload.action] })
      return
    }

    this.pendingTeamMemberAction.set(null)
    this.pendingCancelInvitation.set(null)

    if (payload.action === USER_STATUS_ACTION.CANCEL_INVITATION) {
      this.pendingCancelInvitation.set(payload.user)
      if (!this._modalService.isModalOpen(this.cancelInvitationModalId)) {
        this._modalService.toggleModal(this.cancelInvitationModalId)
      }
      return
    }

    this.pendingTeamMemberAction.set(payload)
    this.showTeamMemberActionModal.set(true)
  }

  /**
   * Returns whether a modal is currently open in the modal service.
   * @param id Modal identifier
   */
  public isModalOpened(id: string): boolean {
    return this._modalService.isModalOpen(id)
  }

  /**
   * Confirms cancel invitation and forwards the remove action to the parent.
   */
  public onCancelInvitationConfirmed(): void {
    const user = this.pendingCancelInvitation()
    if (!user) return

    if (this._modalService.isModalOpen(this.cancelInvitationModalId)) {
      this._modalService.toggleModal(this.cancelInvitationModalId)
    }
    this.updateTeamMember.emit({ id: user.id, action: 'remove' })
    this.pendingCancelInvitation.set(null)
  }

  /**
   * Closes the cancel invitation confirmation modal without confirming.
   */
  public onCancelInvitationCancelled(): void {
    this.pendingCancelInvitation.set(null)
    if (this._modalService.isModalOpen(this.cancelInvitationModalId)) {
      this._modalService.toggleModal(this.cancelInvitationModalId)
    }
  }

  /**
   * Closes the team member action confirmation modal.
   */
  public onTeamMemberActionModalClosed(): void {
    this.showTeamMemberActionModal.set(false)
    this.pendingTeamMemberAction.set(null)
    this._modalService.unregister(TEAM_MEMBER_ACTION_MODAL_ID)
  }

  /**
   * Forwards confirmed team member action to parent container.
   * @param payload User invite ID, API action, and optional notify flag
   * @returns {void}
   */
  public onTeamMemberActionConfirmed(payload: TeamMemberActionPayload): void {
    this.updateTeamMember.emit(payload)
    this.onTeamMemberActionModalClosed()
  }

  /**
   * Forwards team member role update to parent container.
   * @param {number} id Team member invite ID
   * @param {USER_ROLES} role Team member role
   * @returns {void}
   */
  public onUpdateTeamMemberRole(payload: { user: OrganizationUser; role: USER_ROLES }): void {
    if (this.isUpdatingTeamMember()) return

    this.pendingTeamMemberRoleUpdate.set(payload)
    this.showTeamMemberRoleUpdateModal.set(true)
  }

  /**
   * Forwards confirmed team member role update to parent container.
   * @param {number} id Team member invite ID
   * @param {USER_ROLES} role Team member role
   * @returns {void}
   */
  public onTeamMemberRoleUpdateConfirmed(payload: { id: number; role: USER_ROLES }): void {
    this.updateTeamMemberRole.emit(payload)
    this.onTeamMemberRoleUpdateModalClosed()
  }

  /**
   * Closes the team member role update modal.
   */
  public onTeamMemberRoleUpdateModalClosed(): void {
    this.showTeamMemberRoleUpdateModal.set(false)
    this.pendingTeamMemberRoleUpdate.set(null)
    this._modalService.unregister(TEAM_MEMBER_ROLE_UPDATE_MODAL_ID)
  }
}
